import React, { useState, useEffect, useMemo } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import './ColumnMapping.css';

function ColumnMapping({ availableColumns, userColumns, suggestedMappings, savedSelectedColumns, savedManualMappings, onColumnsSelected, onBack }) {
  const [parameterMappings, setParameterMappings] = useState({});
  const [autoFlags, setAutoFlags] = useState({});
  const [error, setError] = useState(null);

  // Memoize rendered LaTeX to avoid re-rendering on every update
  const renderedLabels = useMemo(() => {
    const labels = {};
    Object.entries(availableColumns).forEach(([paramKey, info]) => {
      try {
        const cleanLatex = info.label.replace(/^\$+|\$+$/g, '');
        labels[paramKey] = katex.renderToString(cleanLatex, {
          throwOnError: false,
          displayMode: false
        });
      } catch (e) {
        console.error('KaTeX render error:', e);
        labels[paramKey] = info.label;
      }
    });
    return labels;
  }, [availableColumns]);

  useEffect(() => {
    // Initialize mappings from suggestions
    const initialMappings = {};
    const initialAutoFlags = {};
    
    if (savedManualMappings && Object.keys(savedManualMappings).length > 0) {
      // Restore saved mappings (inverse: user_col -> param, we need param -> user_col)
      Object.entries(savedManualMappings).forEach(([userCol, paramKey]) => {
        initialMappings[paramKey] = userCol;
        initialAutoFlags[paramKey] = false; // Previously saved mappings are not auto
      });
    } else {
      // Initialize from suggestions
      suggestedMappings.forEach(mapping => {
        if (mapping.suggested_column && mapping.confidence === 'high') {
          initialMappings[mapping.suggested_column] = mapping.user_column;
          initialAutoFlags[mapping.suggested_column] = true; // Auto-generated
        }
      });
    }
    
    setParameterMappings(initialMappings);
    setAutoFlags(initialAutoFlags);
  }, [suggestedMappings, savedManualMappings]);

  const handleMappingChange = (paramKey, userColumn) => {
    setParameterMappings(prev => {
      const newMappings = { ...prev };
      if (userColumn === '' || userColumn === 'none') {
        delete newMappings[paramKey];
      } else {
        newMappings[paramKey] = userColumn;
      }
      return newMappings;
    });
    
    // Mark as manually changed (no longer auto)
    setAutoFlags(prev => ({
      ...prev,
      [paramKey]: false
    }));
  };

  const handleContinue = () => {
    // Get selected columns (those with mappings)
    const selectedColumns = Object.keys(parameterMappings).filter(key => parameterMappings[key]);
    
    if (selectedColumns.length === 0) {
      setError('Please map at least one parameter to your CSV columns');
      return;
    }

    // Create reverse mapping for compatibility (userCol -> paramKey)
    const manualMappings = {};
    Object.entries(parameterMappings).forEach(([paramKey, userCol]) => {
      if (userCol) {
        manualMappings[userCol] = paramKey;
      }
    });

    // Create column mappings
    const mappings = selectedColumns.map(col => ({
      user_column: parameterMappings[col],
      mapped_column: col
    }));

    onColumnsSelected(selectedColumns, mappings, manualMappings);
  };

  const getConfidenceBadge = (confidence) => {
    const badges = {
      high: { class: 'badge-high', text: 'High Match' },
      medium: { class: 'badge-medium', text: 'Medium Match' },
      low: { class: 'badge-low', text: 'No Match' }
    };
    return badges[confidence] || badges.low;
  };

  return (
    <div className="column-mapping">
      <h2 className="section-title">Select Columns for Training</h2>
      <p className="section-subtitle">
        Map your CSV columns to the available parameters.
        We've automatically suggested matches where possible.
      </p>

      <div className="mapping-container">
        <div className="mapping-section">
          <h3>Available Parameters</h3>
          <p className="help-text">Map your CSV columns to these parameters. Auto-generated mappings are marked with "auto".</p>
          
          <div className="parameters-list">
            {Object.entries(availableColumns)
              .sort(([keyA], [keyB]) => {
                const hasMappingA = parameterMappings[keyA] ? 1 : 0;
                const hasMappingB = parameterMappings[keyB] ? 1 : 0;
                return hasMappingB - hasMappingA; // Sort mapped items first
              })
              .map(([paramKey, info]) => {
              const currentMapping = parameterMappings[paramKey];
              const isAuto = autoFlags[paramKey];
              const mapping = suggestedMappings.find(m => m.suggested_column === paramKey);
              
              return (
                <div key={paramKey} className="parameter-item">
                  <div className="parameter-header">
                    <div className="parameter-title">
                      <span className="parameter-heading">{info.heading}</span>
                      {isAuto && currentMapping && (
                        <span className="auto-badge">auto</span>
                      )}
                    </div>
                    <span 
                      className="parameter-label"
                      dangerouslySetInnerHTML={{ __html: renderedLabels[paramKey] }}
                    />
                  </div>
                  
                  <div className="parameter-description">{info.description}</div>
                  
                  <div className="parameter-mapping">
                    <label>Map to your column:</label>
                    <select 
                      value={currentMapping || 'none'}
                      onChange={(e) => handleMappingChange(paramKey, e.target.value)}
                      className="mapping-select"
                    >
                      <option value="none">-- None --</option>
                      {userColumns.map(userCol => (
                        <option key={userCol} value={userCol}>
                          {userCol}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {mapping && mapping.user_column && currentMapping === mapping.user_column && (
                    <div className="suggested-info">
                      ✓ Suggested mapping (confidence: {mapping.confidence})
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="selection-summary">
        <strong>{Object.keys(parameterMappings).filter(k => parameterMappings[k]).length}</strong> parameter{Object.keys(parameterMappings).filter(k => parameterMappings[k]).length !== 1 ? 's' : ''} mapped
      </div>

      {error && <div className="error">{error}</div>}

      <div className="actions">
        <button
          className="button button-secondary"
          onClick={onBack}
        >
          ← Back
        </button>
        <button
          className="button"
          onClick={handleContinue}
          disabled={Object.keys(parameterMappings).filter(k => parameterMappings[k]).length === 0}
        >
          Continue to Training →
        </button>
      </div>
    </div>
  );
}

export default ColumnMapping;
